/**
 * Drawdown System Types for Frontend
 *
 * Types for capital call management, payment processing, and general ledger
 * Matches the backend Pydantic schemas for API consistency
 */

// Drawdown Type Enum matching backend
export enum DrawdownType {
  VALUE = 'VALUE',
  PERCENTAGE = 'PERCENTAGE',
}

interface DrawdownBase {
  total_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  purpose: string;
  description?: string;
  percentage_value?: number;
  has_prepayment?: boolean;
  prepayment_amount?: number;
}

export interface DrawdownCreate extends DrawdownBase {
  fund_id: string;
  drawdown_type: DrawdownType;
  amount_per_lp?: number;
  investor_allocations?: DrawdownInvestorCreate[];
}

export interface DrawdownCreateResponse {
  id: string;
  drawdown_number: number;
  status: string;
  total_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  created_at: string;
}

export interface DrawdownUpdateResponse {
  id: string;
  drawdown_number: number;
  status: string;
  total_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  updated_at: string;
}

export interface DrawdownUpdate {
  total_amount?: number;
  currency?: string;
  issue_date?: string;
  due_date?: string;
  purpose?: string;
  description?: string;
  status?: string;
}

export interface Drawdown extends DrawdownBase {
  id: string;
  fund_id: string;
  drawdown_number: number;
  drawdown_type: DrawdownType;
  amount_per_lp?: number;
  percentage_value?: number;
  has_prepayment: boolean;
  prepayment_amount?: number;
  prepayment_pool_usage: number;
  net_new_capital_call: number;
  status: 'draft' | 'issued' | 'partial' | 'completed' | 'cancelled';
  pdf_generated: boolean;
  pdf_path?: string;
  pdf_generated_at?: string;
  approved_by?: string;
  approved_at?: string;
  total_amount_received: number;
  investors_paid_count: number;
  investors_total_count: number;
  batch_count: number;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;

  // Related data
  drawdown_investors: DrawdownInvestor[];
  fund?: Fund;
}

export interface DrawdownSummary {
  id: string;
  fund_id: string;
  drawdown_number: number;
  total_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  purpose: string;
  description?: string;
  status: string;
  pdf_generated: boolean;
  total_amount_received: number;
  investors_paid_count: number;
  investors_total_count: number;
  batch_count: number;
  raised_by_parent_fund: boolean;
  created_at: string;
}

interface DrawdownInvestorBase {
  commitment_amount: number;
  drawdown_percentage: number;
  drawdown_amount: number;
  exemption_reason?: string;
  notes?: string;
}

interface DrawdownInvestorCreate extends DrawdownInvestorBase {
  investor_id: string;
  fund_relationship_id: string;
  prepayment_used?: number;
  net_amount_due?: number;
}

export interface DrawdownInvestor extends DrawdownInvestorBase {
  id: string;
  drawdown_id: string;
  investor_id: string;
  fund_relationship_id: string;
  amount_paid: number;
  payment_count: number;
  first_payment_date?: string;
  last_payment_date?: string;
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'exempted';
  days_overdue: number;
  late_fee_amount: number;
  created_at: string;
  updated_at: string;

  // Related data
  payments: DrawdownPayment[];
  investor?: Investor;
}

/** What the API sends for a payment: Decimal is serialised as a string. */
export interface ApiDrawdownPayment extends Omit<DrawdownPayment, 'amount'> {
  amount: string;
}

export interface ApiDrawdownInvestor extends Omit<
  DrawdownInvestor,
  | 'commitment_amount'
  | 'drawdown_percentage'
  | 'drawdown_amount'
  | 'amount_paid'
  | 'late_fee_amount'
  | 'payments'
> {
  payments?: ApiDrawdownPayment[];
  commitment_amount: string;
  drawdown_percentage: string;
  drawdown_amount: string;
  amount_paid: string;
  late_fee_amount: string;
}

interface DrawdownPaymentBase {
  amount: number;
  currency: string;
  payment_date: string;
  value_date?: string;
  payment_method: 'wire_transfer' | 'check' | 'ach' | 'sepa' | 'swift';
  reference_number?: string;
  wire_details?: Record<string, unknown>;
  bank_statement_line?: string;
  notes?: string;
  processing_status:
    'received' | 'processing' | 'cleared' | 'failed' | 'reversed';
}

interface DrawdownPaymentCreate extends DrawdownPaymentBase {
  drawdown_investor_id: string;
}

export interface DrawdownPaymentUpdate {
  amount?: number;
  currency?: string;
  payment_date?: string;
  value_date?: string;
  payment_method?: string;
  reference_number?: string;
  wire_details?: Record<string, unknown>;
  bank_statement_line?: string;
  notes?: string;
  processing_status?: string;
  reconciled?: boolean;
}

export interface DrawdownPayment extends DrawdownPaymentBase {
  id: string;
  drawdown_investor_id: string;
  /** The staged bank line this payment was settled from. Read-only: only
   *  reconciliation sets it, so it is absent from the create payload. */
  bank_incoming_id?: string | null;
  reconciled: boolean;
  reconciled_at?: string;
  reconciled_by?: string;
  recorded_by?: string;
  /** Absent for the scheduled bank sync's payments, which nobody records. */
  recorded_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

interface GeneralLedgerBase {
  document_date: string;
  posting_date: string;
  account_code: string;
  account_name: string;
  debit_amount: number;
  credit_amount: number;
  currency: string;
  /**
   * Signed movement in `fx_currency`, null on functional-currency lines.
   * Zero on a retranslation: the currency balance holds, its carrying value
   * in `debit_amount`/`credit_amount` is what moved.
   */
  fx_amount?: string | null;
  fx_currency?: string | null;
  fx_rate?: string | null;
  description: string;
  counterparty_name?: string | null;
  reference_type:
    | 'drawdown'
    | 'payment'
    | 'distribution'
    | 'adjustment'
    | 'currency_conversion'
    | 'fx_retranslation';
  /** Null on a draft: numbers are taken on approval. */
  journal_entry_number: string | null;
  investor_id?: string;
  notes?: string;
  reference?: string;
}

export type LedgerEntryState = 'draft' | 'approved' | 'posted' | 'reversed';

/** posted: posted only; approved: posted + approved; all: adds drafts. */
export type LedgerView = 'posted' | 'approved' | 'all';

export const DEFAULT_LEDGER_VIEW: LedgerView = 'approved';

export interface GeneralLedger extends GeneralLedgerBase {
  id: string;
  fund_id: string;
  reference_id: string;
  transaction_id?: string | null;
  state: LedgerEntryState;
  posted_by?: string;
  created_at: string;
  updated_at: string;
}

// API Response Types
export interface DrawdownListResponse {
  drawdowns: DrawdownSummary[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface DrawdownDetailResponse {
  drawdown: Drawdown;
  payment_summary: PaymentSummary;
  overdue_investors: DrawdownInvestor[];
  issue_validation?: DrawdownIssueValidation | null;
  /** False once the call's fund transaction is approved: the ledger is
   *  written, so its payments may no longer be unlinked. */
  can_change_bank_confirmation: boolean;
}

interface DrawdownIssueValidationItem {
  drawdown_investor_id: string;
  investor_name: string;
  has_reference_number: boolean;
  has_investor_iban: boolean;
}

interface DrawdownIssueBlockingTransfer {
  fund_transaction_id: string;
  description: string;
  effective_date: string;
  status: FundTransactionStatus;
  transferor_name: string;
}

export interface DrawdownIssueValidation {
  can_issue: boolean;
  total_investors: number;
  missing_reference_number_count: number;
  missing_investor_iban_count: number;
  has_capital_call_recipient: boolean;
  blocking_items: DrawdownIssueValidationItem[];
  blocking_transfers: DrawdownIssueBlockingTransfer[];
}

interface PaymentSummary {
  total_due: number;
  total_received: number;
  total_outstanding: number;
  completion_percentage: number;
  paid_count: number;
  partial_count: number;
  pending_count: number;
  overdue_count: number;
  total_investors: number;
  overdue_amount: number;
}

export interface LedgerResponse {
  entries: GeneralLedger[];
  total_count: number;
  page: number;
  page_size: number;
  debit_total: number;
  credit_total: number;
  balance: number;
}

export interface TrialBalance {
  accounts: AccountBalance[];
  totals: {
    total_debits: number;
    total_credits: number;
    balance_difference: number;
  };
  as_of_date: string;
}

interface AccountBalance {
  account_code: string;
  account_name: string;
  debit_total: number;
  credit_total: number;
  net_balance: number;
  balance_type: 'debit' | 'credit';
}

export interface ChartOfAccounts {
  chart_of_accounts: Record<string, string>;
  categories: {
    Assets: string[];
    Liabilities: string[];
    Equity: string[];
    Income: string[];
    Expenses: string[];
  };
}

// Request Types
export interface DrawdownStatusUpdate {
  status: string;
}

export interface RecordFullPaymentRequest {
  payment_date: string;
  payment_method: 'wire_transfer' | 'check' | 'ach' | 'sepa' | 'swift';
  reference_number?: string;
  notes?: string;
  bank_statement_reference?: string;
}

export interface RecordFullPaymentResponse {
  message: string;
  payment_id: string;
  drawdown_investor_id: string;
  investor_status: string;
  amount_paid: number;
}

export type PaymentRecordRequest = DrawdownPaymentCreate;

export interface BulkPaymentRequest {
  payments: DrawdownPaymentCreate[];
}

export interface GeneratePDFRequest {
  regenerate: boolean;
  include_payment_instructions: boolean;
}

export interface LedgerQueryParams {
  fund_id?: string;
  account_code?: string;
  reference_type?: string;
  reference_id?: string;
  reference?: string;
  counterparty?: string;
  date_from?: string;
  date_to?: string;
  investor_id?: string;
  view?: LedgerView;
  page: number;
  page_size: number;
}

// Statistics and Reporting Types
export interface PaymentStatistics {
  total_payments: number;
  total_amount: number;
  reconciled_count: number;
  reconciled_amount: number;
  cleared_count: number;
  cleared_amount: number;
  reconciliation_rate: number;
  clear_rate: number;
}

// The statuses an investor can be in while still owing money on a drawdown
export type OverduePaymentStatus = 'pending' | 'partial';

export interface OverduePaymentInfo {
  drawdown_id: string;
  drawdown_number: number;
  fund_name: string;
  investor_id: string;
  investor_name: string;
  due_date: string;
  days_overdue: number;
  amount_due: number;
  amount_paid: number;
  outstanding_amount: number;
  status: string;
}

export interface BalanceSheet {
  as_of_date: string;
  assets: AccountBalance[];
  liabilities: AccountBalance[];
  equity: AccountBalance[];
  prepayment_pool_balance?: number;
  totals: {
    total_assets: number;
    total_liabilities: number;
    total_equity: number;
    balance_check: boolean;
  };
}

export interface IncomeStatement {
  period_from: string;
  period_to: string;
  income: Record<string, AccountBalance>;
  expenses: Record<string, AccountBalance>;
  totals: {
    total_income: number;
    total_expenses: number;
    net_income: number;
  };
}

// UI State Types
export interface DrawdownFilters {
  fund_id?: string;
  status?: string;
  payment_status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface LedgerFilters {
  fund_id?: string;
  account_code?: string;
  reference_type?: string;
  date_from?: string;
  date_to?: string;
  investor_id?: string;
  reference?: string;
  counterparty?: string;
  view?: LedgerView;
}

// ==================== Prepayment Pool Types ====================

interface PrepaymentPoolBase {
  available_balance: number;
  total_contributed: number;
  total_consumed: number;
  currency: string;
}

export interface PrepaymentPool extends PrepaymentPoolBase {
  id: string;
  fund_id: string;
  investor_id: string;
  created_at: string;
  updated_at: string;
}

interface PrepaymentTransactionBase {
  transaction_type: 'CONTRIBUTION' | 'CONSUMPTION';
  amount: number;
  balance_after: number;
  currency: string;
  description?: string;
}

export interface PrepaymentTransaction extends PrepaymentTransactionBase {
  id: string;
  prepayment_pool_id: string;
  drawdown_id: string;
  created_at: string;
  updated_at: string;
}

export interface InvestorPoolItem {
  investor_id: string;
  investor_name: string;
  available_balance: number;
  total_contributed: number;
  total_consumed: number;
  currency: string;
  last_transaction_date?: string;
}

export interface PageResult<T> {
  items: T[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface PrepaymentPoolSummary {
  fund_id: string;
  summary: {
    total_available_balance: number;
    total_lifetime_contributed: number;
    total_lifetime_consumed: number;
    investor_count_with_balance: number;
    total_investor_count: number;
  };
  investor_pools: Array<{
    investor_id: string;
    investor_name: string;
    available_balance: number;
    total_contributed: number;
    total_consumed: number;
    currency: string;
    last_transaction_date?: string;
  }>;
  recent_transactions: Array<{
    id: string;
    transaction_type: string;
    amount: number;
    balance_after: number;
    currency: string;
    description?: string;
    created_at: string;
    drawdown_id: string;
  }>;
}

export interface PrepaymentContributionRequest {
  investor_id: string;
  amount: number;
  currency: string;
  description?: string;
}

export interface PrepaymentConsumptionRequest {
  investor_id: string;
  amount: number;
  currency: string;
  description?: string;
}

// ==================== Drawdown Batch Types ====================

interface DrawdownBatchBase {
  batch_number: number;
  batch_name: string;
  notice_date: string | null;
  due_date: string;
}

export interface DrawdownBatchCreate extends DrawdownBatchBase {
  drawdown_id: string;
}

export interface DrawdownBatchUpdate {
  batch_name?: string;
  notice_date?: string;
  due_date?: string;
}

export interface DrawdownBatch extends DrawdownBatchBase {
  id: string;
  drawdown_id: string;
  total_amount: number;
  investor_count: number;
  status: 'DRAFT' | 'ISSUED' | 'SENT' | 'COMPLETED';
  actual_notice_date: string | null;
  emails_sent: number;
  emails_total: number;
  emails_failed: number;

  // PDF generation status tracking
  pdf_generation_status:
    'pending' | 'generating' | 'completed' | 'failed' | 'partial';
  pdfs_completed: number;
  pdfs_failed: number;
  pdfs_total: number;

  created_at: string;
  updated_at: string;
}

/** Minimal payload returned by batch status / issue endpoints */
export interface DrawdownBatchUpdateResponse {
  id: string;
  batch_number: number;
  status: DrawdownBatch['status'];
  updated_at: string;
}

export interface BatchInvestorNotice {
  id: string;
  investor_id: string;
  investor_name: string;
  investor_iban: string | null;
  is_fund_investor: boolean;
  commitment_amount: number;
  drawdown_percentage: number;
  drawdown_amount: number;
  prepayment_used: number;
  net_amount_due: number;
  amount_due: number;
  notification_id: string | null;
  pdf_generated: boolean;
  document_id: string | null;
  email_sent: boolean;
  status: string;
  reference_number: string | null;
  has_reference_number: boolean;
  has_investor_iban: boolean;
  reconciled_amount: number;
  verification_status: 'draft' | 'posted';

  // PDF generation status tracking
  pdf_generation_status: 'pending' | 'generating' | 'completed' | 'failed';
  pdf_generation_error: string | null;
  last_pdf_generation_attempt: string | null;
}

export interface BatchPreviewResponse {
  batch: DrawdownBatch;
  investor_notices: BatchInvestorNotice[];
}

// ==================== Drawdown Recalculation Types ====================

interface RecalculationInvestorAdd {
  investor_id: string;
  notice_date: string;
  due_date: string;
}

export interface DrawdownRecalculationRequest {
  added_investors: RecalculationInvestorAdd[];
  preserve_sent_notices: boolean;
  regenerate_dates: boolean;
}

export interface DrawdownRecalculationResponse {
  drawdown_id: string;
  new_batch_count: number;
  new_investor_count: number;
  message: string;
}

// ==================== Grouped Drawdown View Types ====================

interface DrawdownBatchView {
  batchId: string;
  batchNumber: number;
  noticeDate: string | null;
  dueDate: string;
  investors: number;
  amount: number;
  status: 'DRAFT' | 'ISSUED' | 'SENT' | 'COMPLETED';
  invoicesSent: number;
  invoicesTotal: number;
  pdfGenerated: boolean;

  // PDF generation status tracking
  pdf_generation_status?:
    'pending' | 'generating' | 'completed' | 'failed' | 'partial';
  pdfs_completed?: number;
  pdfs_failed?: number;
  pdfs_total?: number;
}

export interface DrawdownGroup {
  drawdownId: string;
  drawdownNumber: string;
  fundName: string;
  purpose: string;
  description?: string;
  totalAmount: number;
  status: string;
  issueDate: string;
  batches: DrawdownBatchView[];
  createdAt: string;
  lastUpdated: string;
  hasRecalculationNeeded?: boolean; // Shows blue dot if true
  // Aggregate counts (available even when batches aren't loaded)
  totalInvestorCount: number;
  totalBatchCount: number;
  // Raised automatically to fund a call in the fund above; its total belongs to
  // that fund, so the local edits are off the table
  raisedByParentFund?: boolean;
}

// Import the Fund and Investor types from existing modules
import type { Fund } from './fund';
import type { FundTransactionStatus } from './fund-transactions';

interface Investor {
  id: string;
  name: string;
  email?: string;
  // Add other investor properties as needed
}

// ==================== Transaction Types ====================

/**
 * Transaction types matching backend TransactionType enum.
 * Represents different types of financial activities in a fund.
 */
export enum TransactionType {
  DRAWDOWN = 'drawdown',
  INVESTMENT_PURCHASE = 'investment_purchase',
  FUND_FEE_EXPENSE = 'fund_fee_expense',
  DISTRIBUTION = 'distribution',
  LP_TRANSFER = 'lp_transfer',
  MANUAL_ADJUSTMENT = 'manual_adjustment',
  VALUATION_CHANGE = 'valuation_change',
  ASSET_SALE = 'asset_sale',
  MANAGER_CURRENT_ACCOUNT = 'manager_current_account',
}

/**
 * Transaction status matching backend TransactionStatus enum.
 */
export enum TransactionStatus {
  APPROVED = 'approved',
  POSTED = 'posted',
  REVERSED = 'reversed',
}

/**
 * Transaction response from API.
 * Represents a business event in the fund.
 */
export interface Transaction {
  id: string;
  fund_id: string;
  transaction_type: TransactionType;
  reference_id: string;
  transaction_date: string;
  posting_date: string;
  total_amount: number;
  currency: string;
  description: string;
  status: TransactionStatus;
  reference?: string | null;
  portfolio_company_name?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Transaction detail response including journal entries.
 */
export interface TransactionDetail extends Transaction {
  journal_entries: GeneralLedger[];
}

/**
 * Paginated list of transactions response.
 */
export interface TransactionsListResponse {
  transactions: Transaction[];
  total_count: number;
  page: number;
  page_size: number;
}

/**
 * Query parameters for listing transactions.
 */
export interface TransactionQueryParams {
  fund_id?: string;
  transaction_type?: TransactionType;
  date_from?: string;
  date_to?: string;
  status?: TransactionStatus;
  reference?: string;
  page?: number;
  page_size?: number;
}

// ==================== Drawdown Payment Matrix Types ====================

export type DrawdownMatrixCellStatus =
  'pending' | 'partial' | 'paid' | 'overdue' | 'exempted' | 'draft';

/** One investor's position on one capital call. */
export interface DrawdownMatrixCell {
  amount_due: number;
  amount_paid: number;
  status: DrawdownMatrixCellStatus;
  /** Due date of the batch this investor sits in */
  due_date: string;
  days_overdue: number;
}

export interface DrawdownMatrixColumn {
  drawdown_id: string;
  drawdown_number: number;
  purpose: string;
  status: Drawdown['status'];
  due_date: string;
  /** Days the latest unpaid investor on this call is late */
  days_overdue: number;
}

/** Called and paid-in an LP transfer moved; signed, negative when out. */
export interface DrawdownMatrixTransfer {
  fund_transaction_id: string;
  effective_date: string;
  direction: 'in' | 'out';
  counterparty_name: string;
  amount_called: number;
  amount_paid: number;
}

/** Totals include what LP transfers moved. */
export interface DrawdownMatrixRow {
  investor_id: string;
  investor_name: string;
  commitment_amount: number;
  amount_due: number;
  amount_paid: number;
  outstanding: number;
  /** Keyed by drawdown id; absent where the investor was not called */
  cells: Record<string, DrawdownMatrixCell>;
  transfers: DrawdownMatrixTransfer[];
}

export interface DrawdownMatrix {
  currency: string;
  /** The fund's committed capital: active relationships only */
  committed: number;
  columns: DrawdownMatrixColumn[];
  rows: DrawdownMatrixRow[];
}
